import { useMemo, useState } from 'react';
import { useQueueDequeue } from '../contexts/QueueDequeueContext';
import { formatEta, fullName, partyMarker } from '../services/queue';
import LanguageFlag from './LanguageFlag';
import type { QueueEntry } from '../types/database';

interface Props {
  onClose: () => void;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', {
    timeZone: 'Europe/Amsterdam',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function entryLabel(e: QueueEntry): string {
  const marker = partyMarker(e);
  const exp = e.experience_names ?? '—';
  return `${fullName(e)}${marker ? ` (${marker})` : ''} · ${exp}`;
}

export default function QueuePickerModal({ onClose }: Props) {
  const { entries, selectedDequeueEntryId, headEntry, setSelected } = useQueueDequeue();
  // Local mirror so the click→confirm pattern doesn't write to context on each radio change.
  const [picked, setPicked] = useState<string | null>(
    selectedDequeueEntryId ?? headEntry?.queue_entry_id ?? null,
  );

  // Parked ("even weg") visitors keep their spot in the queue but are listed
  // last here: they are the exception, not the next pick.
  const ordered = useMemo(
    () => [...entries.filter((e) => !e.parked), ...entries.filter((e) => e.parked)],
    [entries],
  );

  function confirm() {
    // If the user picked the current head, treat it as "follow head" by
    // clearing the override — this way if a new arrival jumps to head, the
    // dequeue follows automatically.
    if (picked && headEntry && picked === headEntry.queue_entry_id) {
      setSelected(null);
    } else {
      setSelected(picked);
    }
    onClose();
  }

  return (
    <div className="queue-picker-overlay" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="queue-picker-modal" onClick={(e) => e.stopPropagation()}>
        <header className="queue-picker-header">
          <h2>Selecteer wie uit de wachtrij gaat</h2>
          <button className="queue-picker-close" onClick={onClose} aria-label="Sluiten">
            ✕
          </button>
        </header>

        {entries.length === 0 ? (
          <div className="queue-picker-empty">Geen klanten in de wachtrij.</div>
        ) : (
          <ul className="queue-picker-list">
            {ordered.map((e) => (
              <li key={e.queue_entry_id}>
                <label className={`queue-picker-row ${picked === e.queue_entry_id ? 'selected' : ''} ${e.parked ? 'queue-picker-row--parked' : ''}`}>
                  <input
                    type="radio"
                    name="queue-pick"
                    checked={picked === e.queue_entry_id}
                    onChange={() => setPicked(e.queue_entry_id)}
                  />
                  <span className="queue-picker-pos">{e.position}</span>
                  <span className="queue-picker-time">{formatTime(e.departure_start)}</span>
                  <span className="queue-picker-label">
                    <LanguageFlag code={e.play_language} className="queue-row-flag" />
                    {entryLabel(e)}
                    {e.parked ? (
                      <span className="queue-parked-pill">even weg</span>
                    ) : e.eta_minutes !== null && e.eta_minutes > 0 ? (
                      <span className="queue-eta">{formatEta(e.eta_minutes)}</span>
                    ) : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}

        <footer className="queue-picker-footer">
          <button className="queue-picker-secondary" onClick={onClose}>
            Annuleren
          </button>
          <button
            className="queue-picker-primary"
            onClick={confirm}
            disabled={entries.length === 0}
          >
            Selecteren
          </button>
        </footer>
      </div>
    </div>
  );
}
